<script setup lang="ts">
import { Home, Mail, Phone } from "lucide-vue-next";
import type {
  PublicWorkerProfile,
  PublicWorkerReviewsResponse,
  PublicWorkerServicesResponse,
} from "~/types/public-worker";
import { getCategorySearchPath } from "~/utils/search-catalog";
import { formatPersonName } from "~/utils/worker-profile";

const props = defineProps<{
  worker: PublicWorkerProfile;
  servicesData: PublicWorkerServicesResponse;
  reviews: PublicWorkerReviewsResponse;
}>();

const router = useRouter();
const localeRoute = useLocaleRoute();
const { t } = useI18n();
const toast = useToast();

const navSticky = ref(false);
const footerVisible = ref(false);

let footerObserver: IntersectionObserver | null = null;

const fullName = computed(() =>
  formatPersonName(props.worker.name, props.worker.lastname),
);

const hasMobileContacts = computed(() =>
  Boolean(props.worker.contactPhone || props.worker.contactEmail),
);

const showMobileActions = computed(
  () => hasMobileContacts.value && !footerVisible.value,
);

function catalogLabel(type: "category" | "region", value: string) {
  const key = `${type}.${value}`;
  const translated = t(key);

  return translated === key ? value : translated;
}

const breadcrumbs = computed(() => {
  const items: Array<{
    label: string;
    to: ReturnType<typeof localeRoute> | string | null;
  }> = [
    { label: t("search.page.home"), to: localeRoute("/") || "/" },
    {
      label: t("search.page.workers"),
      to: localeRoute("/workers") || "/workers",
    },
  ];

  const category = props.worker.primaryCategoryName;
  const region =
    props.worker.coverageType === "nationwide"
      ? null
      : props.worker.primaryRegionName;

  if (category) {
    const categoryPath = getCategorySearchPath(category);

    items.push({
      label: catalogLabel("category", category),
      to: localeRoute(categoryPath) || categoryPath,
    });

    if (region) {
      const path = getCategorySearchPath(category, region);

      items.push({
        label: catalogLabel("region", region),
        to: localeRoute(path) || path,
      });
    }
  }

  items.push({ label: fullName.value, to: null });

  return items;
});

function goBack() {
  if (typeof window !== "undefined" && window.history.length > 1) {
    router.back();
    return;
  }

  navigateTo(localeRoute("/workers") || "/workers");
}

async function shareProfile() {
  if (typeof window === "undefined") return;

  const url = window.location.href.split("#")[0];

  try {
    if (navigator.share) {
      await navigator.share({ title: fullName.value, url });
      return;
    }

    await navigator.clipboard.writeText(url);

    toast.success({
      title: t("worker_profile.link_copied_title"),
      description: t("worker_profile.link_copied_description"),
    });
  } catch (error: any) {
    if (error?.name === "AbortError") return;

    toast.error({
      title: t("worker_profile.share_error_title"),
      description: t("worker_profile.share_error_description"),
    });
  }
}

onMounted(() => {
  const footer = document.querySelector("footer");
  if (!footer) return;

  footerObserver = new IntersectionObserver(
    ([entry]) => {
      footerVisible.value = entry.isIntersecting;
    },
    {
      threshold: 0.01,
    },
  );

  footerObserver.observe(footer);
});

onBeforeUnmount(() => {
  footerObserver?.disconnect();
});
</script>

<template>
  <div class="worker-public-profile bg-white">
    <h1 class="sr-only">{{ fullName }}</h1>

    <WorkerPublicProfileToolbar
      class="worker-profile-toolbar"
      @back="goBack"
      @share="shareProfile"
    />

    <div class="hidden border-b border-app-border bg-white md:block">
      <div class="mx-auto w-full max-w-[1280px] px-4 sm:px-6">
        <div class="profile-breadcrumb-scroll overflow-x-auto">
          <nav
            aria-label="Breadcrumb"
            class="flex min-h-11 w-max min-w-full items-center gap-2 py-2 text-xs sm:text-sm"
          >
            <template v-for="(crumb, index) in breadcrumbs" :key="crumb.label">
              <NuxtLink
                v-if="crumb.to"
                :to="crumb.to"
                class="inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-medium text-primary transition hover:text-primary-700"
              >
                <Home v-if="index === 0" class="size-3.5" />
                {{ crumb.label }}
              </NuxtLink>

              <span
                v-else
                class="shrink-0 whitespace-nowrap font-semibold text-app-text"
              >
                {{ crumb.label }}
              </span>

              <span
                v-if="index < breadcrumbs.length - 1"
                class="shrink-0 text-primary/35"
              >
                /
              </span>
            </template>
          </nav>
        </div>
      </div>
    </div>

    <div
      class="mx-auto w-full max-w-[1280px] px-4 pb-16 pt-5 sm:px-6 sm:pb-16 sm:pt-7 lg:pb-10 lg:pt-8"
    >
      <div
        class="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px] xl:gap-10"
      >
        <main class="min-w-0">
          <div class="mb-5 lg:hidden">
            <WorkerPublicProfileSidebar
              :worker="worker"
              :review-summary="reviews.summary"
              summary-only
            />
          </div>

          <WorkerPublicProfileAnchorNav
            :has-experience="Boolean(worker.experiences.length)"
            :has-education="Boolean(worker.educations.length)"
            :has-contacts="Boolean(worker.contactPhone || worker.contactEmail)"
            @sticky-change="navSticky = $event"
          />

          <WorkerPublicAboutSection :worker="worker" />
          <WorkerPublicExperienceSection :items="worker.experiences" />
          <WorkerPublicEducationSection :items="worker.educations" />

          <WorkerPublicServicesSection
            :worker-id="worker.id"
            :initial-data="servicesData"
          />

          <WorkerPublicContactsSection :worker="worker" />

          <WorkerPublicReviewsSection
            :worker-id="worker.id"
            :worker-name="fullName"
            :initial-data="reviews"
          />
        </main>

        <div
          class="hidden self-start transition-[top] duration-200 lg:sticky lg:block"
          :class="navSticky ? 'top-[136px]' : 'top-[84px]'"
        >
          <WorkerPublicProfileSidebar
            :worker="worker"
            :review-summary="reviews.summary"
          />
        </div>
      </div>

      <WorkerPublicSafetyNotice />
    </div>

    <Transition name="mobile-actions">
      <div
        v-if="showMobileActions"
        class="worker-profile-mobile-actions fixed inset-x-0 bottom-0 z-40 border-t border-app-border bg-white/95 px-3 py-2.5 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden"
      >
        <div class="mx-auto flex max-w-[760px] gap-2">
          <a
            v-if="worker.contactPhone"
            :href="`tel:${worker.contactPhone}`"
            class="flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white"
          >
            <Phone class="size-4" />
            {{ $t("worker_profile.call") }}
          </a>

          <a
            v-if="worker.contactEmail"
            :href="`mailto:${worker.contactEmail}`"
            class="flex h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-primary bg-white px-4 text-sm font-semibold text-primary"
          >
            <Mail class="size-4" />
            {{ $t("worker_profile.email") }}
          </a>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.profile-breadcrumb-scroll {
  scrollbar-width: none;
}

.profile-breadcrumb-scroll::-webkit-scrollbar {
  display: none;
}

.mobile-actions-enter-active,
.mobile-actions-leave-active {
  transition:
    opacity 180ms ease,
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
}

.mobile-actions-enter-from,
.mobile-actions-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

@media print {
  :global(header),
  :global(footer),
  .worker-profile-toolbar,
  .worker-profile-mobile-actions {
    display: none !important;
  }

  .worker-public-profile :deep(.sticky) {
    position: static !important;
  }
}
</style>
